import { useState } from "react";
import { Link } from "react-router";
import { addDays, addMonths } from "date-fns";
import { ArrowRight, CalendarDays, ClipboardCheck, List, Rows3 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import type { AgendaViewMode, WeekAppointment } from "@/lib/dashboard-week";
import type { ClinicOutgoingOrder } from "@/lib/dashboard-lab";
import { MobileAgenda } from "@/components/agenda/mobile/MobileAgenda";
import { AppointmentDetailDialog } from "./AppointmentDetailDialog";
import { ResumoDePedidosDaClinica } from "./ResumoDePedidosDaClinica";
import { ProsthesisOrderDetailDialog } from "@/components/prosthesis/ProsthesisOrderDetailDialog";

type AgendaKind = "consultas" | "pedidos";

type Props = {
  viewMode: AgendaViewMode;
  onViewModeChange: (m: AgendaViewMode) => void;
  /** Compromissos de outras organizações (escopo Pessoal/Organizações do Início) — entram na
   *  mesma grade, só-leitura. A janela deles é a de hoje (a mesma de antes); a navegação de
   *  período cobre integralmente a organização ativa, que se busca sozinha. */
  extraAppointments: WeekAppointment[];
  includeOwn: boolean;
  onSelectForeign?: (appt: WeekAppointment) => void;
};

/**
 * A agenda do Início no celular — a MESMA agenda da página /agenda (MobileAgenda: grade de
 * verdade, cards clicáveis, ‹ período ›), de borda a borda, com os controles ACIMA do container
 * em vez da barra fixa de rodapé que a página usa. A aba Pedidos é o resumo pelo dia de entrega.
 */
export function InicioAgendaMobile({
  viewMode,
  onViewModeChange,
  extraAppointments,
  includeOwn,
  onSelectForeign,
}: Props) {
  const [kind, setKind] = useState<AgendaKind>("consultas");
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [selectedAppt, setSelectedAppt] = useState<WeekAppointment | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const settingsQuery = trpc.settings.getAgenda.useQuery();
  const ordersQuery = trpc.prosthesis.listMineOutgoingDeadlineSummary.useQuery(undefined, {
    enabled: kind === "pedidos",
  });
  const orders = (ordersQuery.data ?? []) as ClinicOutgoingOrder[];

  const navegar = (direcao: 1 | -1) => {
    setAnchorDate((atual) =>
      viewMode === "month"
        ? addMonths(atual, direcao)
        : addDays(atual, direcao * (viewMode === "week" ? 7 : 1)),
    );
  };

  const aoClicarConsulta = (_id: string, appt?: any) => {
    if (!appt) return;
    if (appt.ehDeOutraOrganizacao && onSelectForeign) {
      onSelectForeign(appt);
      return;
    }
    setSelectedAppt(appt);
  };

  return (
    <div className="space-y-3">
      {/* Controles acima do container, no lugar da barra de rodapé da página Agenda. */}
      <div className="flex h-12 w-full items-center rounded-md border border-zinc-200 bg-white p-0.5 dark:border-border dark:bg-card">
        {(
          [
            { key: "consultas", label: "Consultas", icon: CalendarDays },
            { key: "pedidos", label: "Pedidos", icon: ClipboardCheck },
          ] as const
        ).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setKind(key)}
            className={cn(
              "inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded text-sm font-medium transition-colors",
              kind === key
                ? "bg-brand-600 font-semibold text-white"
                : "text-zinc-600 dark:text-muted-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {kind === "consultas" ? (
        settingsQuery.data ? (
          // De borda a borda, igualzinho à página Agenda — escapa do padding do <main>. O seletor
          // de visão é o MESMO tablist da página, grudado na faixa azul do período.
          <div className="-mx-4 sm:-mx-6">
            <SeletorDeVisao viewMode={viewMode} onViewModeChange={onViewModeChange} />
            <MobileAgenda
              embutido
              anchorDate={anchorDate}
              viewMode={viewMode}
              settings={settingsQuery.data}
              changeViewMode={(m) => onViewModeChange(m as AgendaViewMode)}
              navigate={navegar}
              extraAppointments={extraAppointments}
              includeOwn={includeOwn}
              onAppointmentClick={aoClicarConsulta}
            />
          </div>
        ) : (
          <div className="h-40 animate-pulse rounded-lg bg-zinc-100 dark:bg-muted" />
        )
      ) : ordersQuery.isLoading ? (
        <div className="h-40 animate-pulse rounded-lg bg-zinc-100 dark:bg-muted" />
      ) : (
        <div className="space-y-2">
          <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-foreground">Resumo dos pedidos</h2>
          <ResumoDePedidosDaClinica orders={orders} onSelectOrder={(o) => setSelectedOrderId(o.id)} />
        </div>
      )}

      <div className="flex justify-start">
        <Link
          to={kind === "consultas" ? "/agenda" : "/protese"}
          className="-mx-2 inline-flex h-11 items-center gap-1 rounded-md px-2 text-sm font-medium text-brand-700 transition-colors hover:text-brand-800 dark:text-primary"
        >
          {kind === "consultas" ? "Ver agenda completa" : "Ver todos os pedidos"}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      <AppointmentDetailDialog appointment={selectedAppt} onClose={() => setSelectedAppt(null)} />
      <ProsthesisOrderDetailDialog orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} readOnly />
    </div>
  );
}

/** O tablist Mês/Semana/Dia da página Agenda (mesmos ícones e ordem), só que no TOPO do container,
 *  grudado na faixa azul do período — no Início não existe a barra fixa de rodapé. */
function SeletorDeVisao({
  viewMode,
  onViewModeChange,
}: {
  viewMode: AgendaViewMode;
  onViewModeChange: (m: AgendaViewMode) => void;
}) {
  return (
    <nav className="grid grid-cols-3 border-y border-zinc-200 bg-white dark:border-border dark:bg-card">
      {(
        [
          { key: "month", label: "Mês", icon: <CalendarDays className="h-5 w-5" /> },
          { key: "week", label: "Semana", icon: <Rows3 className="h-5 w-5" /> },
          { key: "day", label: "Dia", icon: <List className="h-5 w-5" /> },
        ] as const
      ).map(({ key, label, icon }) => (
        <button
          key={key}
          type="button"
          onClick={() => onViewModeChange(key)}
          className={cn(
            "flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-semibold transition-colors",
            viewMode === key ? "text-brand-600" : "text-zinc-500 active:text-zinc-700 dark:text-muted-foreground",
          )}
        >
          {icon}
          {label}
        </button>
      ))}
    </nav>
  );
}
